import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useFirebase } from '../contexts/FirebaseContext'
import { 
  ArrowLeft, 
  Save, 
  Upload,
  X,
  Image as ImageIcon
} from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'

const UpdateEdit = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  
  const [formData, setFormData] = useState({
    title: '',
    note: '',
    link: '',
    image: null,
    createdDate: new Date().toISOString().split('T')[0]
  })
  const [loading, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(true)
  const [dragActive, setDragActive] = useState(false)
  const [currentImageUrl, setCurrentImageUrl] = useState(null)
  const { getUpdates, updateUpdate, uploadImage } = useFirebase()

  useEffect(() => {
    loadUpdate()
  }, [id])

  const loadUpdate = async () => {
    try {
      setInitialLoading(true)
      const updates = await getUpdates()
      const foundUpdate = updates.find(u => u.id === id)
      if (foundUpdate) {
        setFormData({
          title: foundUpdate.title || '',
          note: foundUpdate.message || '',
          link: foundUpdate.link || '',
          image: null,
          createdDate: foundUpdate.createdDate || new Date().toISOString().split('T')[0]
        })
        // Set current image URL if it exists
        setCurrentImageUrl(foundUpdate.imageUrl || null)
      } else {
        toast.error('Alert not found')
        navigate('/updates')
      }
    } catch (error) {
      console.error('Error loading update:', error)
      toast.error('Failed to load alert')
      navigate('/updates')
    } finally {
      setInitialLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  // Drag and drop handlers
  const handleDrag = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true)
    } else if (e.type === "dragleave") {
      setDragActive(false)
    }
  }, [])

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0]
      if (file.type.startsWith('image/')) {
        setFormData(prev => ({ ...prev, image: file }))
        toast.success('Image uploaded successfully!')
      } else {
        toast.error('Please upload an image file')
      }
    }
  }, [])

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0]
      if (file.type.startsWith('image/')) {
        setFormData(prev => ({ ...prev, image: file }))
        toast.success('Image uploaded successfully!')
      } else {
        toast.error('Please upload an image file')
      }
    }
  }

  const removeImage = () => {
    setFormData(prev => ({ ...prev, image: null }))
    // If there's a new image selected, remove it, otherwise remove the current image URL
    if (formData.image) {
      // Remove newly selected image
    } else {
      // Remove current image URL
      setCurrentImageUrl(null)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!formData.title.trim() || !formData.note.trim()) {
      toast.error('Please fill in title and note')
      return
    }

    try {
      setLoading(true)
      
      let imageUrl = currentImageUrl // Keep existing image URL by default
      
      // Upload new image if one is selected
      if (formData.image) {
        try {
          console.log('📤 Uploading new image to Firebase Storage...')
          imageUrl = await uploadImage(formData.image, 'updates')
          console.log('✅ New image uploaded successfully:', imageUrl)
        } catch (imageError) {
          console.error('❌ Image upload failed:', imageError)
          toast.error('Failed to upload new image. Keeping existing image.')
          // Keep existing image if new upload fails
        }
      }
      
      // Update existing update in Firestore
      const updateData = {
        title: formData.title.trim(),
        message: formData.note.trim(),
        link: formData.link.trim(),
        type: 'announcement',
        status: 'sent',
        priority: 'medium',
        targetAudience: 'all',
        reach: 1200,
        createdDate: formData.createdDate,
        updatedAt: Date.now(),
        imageUrl: imageUrl // Update image URL (existing or new)
      }

      await updateUpdate(id, updateData)
      toast.success('Alert saved successfully!')
      navigate('/updates')
    } catch (error) {
      console.error('Error saving update:', error)
      toast.error('Failed to save alert')
    } finally {
      setLoading(false)
    }
  }

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/updates')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-6 w-6 text-gray-600" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Edit Alert</h1>
        </div>
      </div>

      {/* Form */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">Alert Details</h3>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Image Upload - Drag & Drop */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload Image
              </label>
              <div
                className={`relative border-2 border-dashed rounded-lg p-6 transition-colors ${
                  dragActive 
                    ? 'border-blue-400 bg-blue-50' 
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileInput}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                
                {formData.image ? (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <ImageIcon className="h-8 w-8 text-green-600" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          New: {formData.image.name}
                        </p>
                        <p className="text-sm text-gray-500">
                          {(formData.image.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={removeImage}
                      className="p-1 hover:bg-red-100 rounded-full transition-colors"
                    >
                      <X className="h-5 w-5 text-red-600" />
                    </button>
                  </div>
                ) : currentImageUrl ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-center">
                      <img 
                        src={currentImageUrl} 
                        alt="Current update image"
                        className="max-h-32 rounded-lg shadow-sm"
                      />
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-900 mb-1">Current Image</p>
                      <p className="text-xs text-gray-500 mb-2">
                        Click to upload or drag and drop a new image to replace
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center">
                    <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-sm text-gray-600 mb-2">
                      <span className="font-medium">Click to upload</span> or drag and drop
                    </p>
                    <p className="text-xs text-gray-500">
                      PNG, JPG, GIF up to 10MB
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className="input w-full"
                placeholder="Enter update title..."
                required
              />
            </div>

            {/* Note (Message) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Note *
              </label>
              <textarea
                name="note"
                value={formData.note}
                onChange={handleChange}
                rows={4}
                className="input w-full"
                placeholder="Enter your note..."
                required
              />
            </div>

            {/* Link */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Link
              </label>
              <input
                type="url"
                name="link"
                value={formData.link}
                onChange={handleChange}
                className="input w-full"
                placeholder="Enter link URL (optional)..."
              />
              <p className="mt-1 text-sm text-gray-500">
                Optional: Add a relevant link for this alert
              </p>
            </div>

            {/* Created Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Created Date
              </label>
              <input
                type="date"
                name="createdDate"
                value={formData.createdDate}
                onChange={handleChange}
                className="input w-full"
              />
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end space-x-3 pt-6 border-t">
              <button
                type="button"
                onClick={() => navigate('/updates')}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary flex items-center space-x-2"
              >
                {loading ? (
                  <LoadingSpinner size="small" />
                ) : (
                  <>
                    <Save className="h-5 w-5" />
                    <span>Save Changes</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default UpdateEdit
